import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HouseLine } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { formatApiError } from "@/lib/api";

const AUTH_IMG = "https://static.prod-images.emergentagent.com/jobs/09d18691-9bcf-4619-a866-359b60aa1b93/images/01e660114c63508bee552c6d4f5c92a8d6225e8cd826c1796c2459af97d36c5e.jpeg";

const BUY_LABELS = {
  document_pack: "Document Pack",
  consultation: "30-Minute Consultation",
  deal_closer: "The Deal Closer",
  full_flip_formula: "Full Flip Formula",
  inner_circle: "Inner Circle",
};

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const buy = location.state?.buy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = mode === "login" ? await login(email, password) : await register(name, email, password);
      if (buy) {
        await startCheckout(buy, user, navigate);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="Mobile home architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="overline-label text-white/70">TTPI Academy</p>
          <h2 className="mt-4 max-w-md font-heading text-4xl font-light leading-tight text-white">
            Your next deal starts with a single login.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <Link to="/" data-testid="auth-logo" className="mb-12 flex items-center gap-2">
            <HouseLine size={24} weight="duotone" className="text-primary" />
            <span className="font-heading text-2xl tracking-tight">TTPI Academy</span>
          </Link>

          <p className="overline-label">{mode === "login" ? "Welcome back" : "Join the academy"}</p>
          <h1 data-testid="auth-heading" className="mt-3 font-heading text-4xl font-light">
            {mode === "login" ? "Sign in to your account" : "Create your account"}
          </h1>
          {buy && (
            <p data-testid="auth-buy-notice" className="mt-4 border border-border bg-white p-3 text-sm text-muted-foreground">
              After signing in, you'll continue to checkout for <strong className="text-foreground">{BUY_LABELS[buy] || buy}</strong>.
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em]">Full name</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="rounded-none border-input bg-white"
                  placeholder="Your name"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-input bg-white"
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em]">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="rounded-none border-input bg-white"
                placeholder={mode === "register" ? "Minimum 8 characters" : "Your password"}
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="w-full rounded-none bg-foreground py-6 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-300 hover:bg-primary hover:text-primary-foreground"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            {mode === "login" ? "New to TTPI Academy?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-mode-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary underline-offset-4 transition-colors duration-200 hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
